import React, { useEffect } from "react";
import {
  Container, Box, Typography, Link, Breadcrumbs, Skeleton,
} from '@mui/material';
import { useRouter } from 'next/router';
import { dataConstants } from '../../src/constants';
import getPageMetaTags from '../../lib/meta/_getMetaTags';
import useAuth from "../../src/hook/auth";
import getWatchlist from "../../lib/watchlist/_getWatchlist";
import getStockTabs from "../../lib/stock/_getStockTabsData";
import addToWatchlist from "../../lib/watchlist/_addToWatchlist";
import AddNewWatchlist from "../../src/components/Watchlist/AddNewWatchlist";
import ViewModuleRoundedIcon from '@mui/icons-material/ViewModuleRounded';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import GridView from "../../src/components/Watchlist/GridView";
import ListView from "../../src/components/Watchlist/ListView";
import { getHeroContent } from "../../src/utils/helpers";
import AddRoundedIcon from '@mui/icons-material/AddRounded';

const Watchlist = ({
  watch_tab_data, themeMode, pageLoading, hero,
}) => {
  const router = useRouter();
  const auth = useAuth();
  const { user } = auth || {};
  const [watchlists, setWatchlists] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [alignment, setAlignment] = React.useState('list');

  const handleAlignment = (event, newAlignment) => {
    if (newAlignment !== null) {
      setAlignment(newAlignment);
    }
  };

  const handleNewWatchlistName = async (new_list_name) => {
    setLoading(true);
    const _columns = "['ticker','tickername','sector','MKTCAP','REV_TTM','PCHG__7','PCHG__30','PCHG__365','PE','PS','ROE','REV_TTM__PCHG_3Y','DRAWDN']";
    try {
      const data = await addToWatchlist(new_list_name, [], "", "", _columns, user?.accessToken);
      if (data?.addToWatchlist?.status === "Success") {
        router.push(`/watchlists/u/${new_list_name}`);
      }
    } catch (err) {
      console.log('caught it!', err);
    } finally {
      setLoading(false);
    }
  };

  const getAllWatchlists = async () => {
    setLoading(true);
    try {
      const watchlistWithoutToken = await getWatchlist("");
      const watchlistWithToken = user?.accessToken ? await getWatchlist(user?.accessToken) : null;

      const sortedWatchlists = user?.accessToken && watchlistWithToken?.getWatchlist
        ? [...watchlistWithToken.getWatchlist, ...watchlistWithoutToken.getWatchlist]
            .sort((a, b) => a.isSystemDefault === b.isSystemDefault ? 0 : a.isSystemDefault ? -1 : 1)
        : watchlistWithoutToken?.getWatchlist;

      setWatchlists(sortedWatchlists ?? []);
    } catch (error) {
      console.log('caught it!', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllWatchlists();
  }, [user?.accessToken]);

  const isLoading = loading && watchlists.length === 0;
  const isEmpty = !loading && watchlists.length === 0;

  return (
    <Box sx={{ minHeight: '80vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: themeMode === 'dark' ? 'background.default' : 'background.paper',
      }}>
        <Container maxWidth="lg">
          <Box sx={{ pt: 3, pb: 0.5 }}>
            <Breadcrumbs sx={{ mb: 2 }}>
              <Link
                underline="hover"
                color="text.secondary"
                href={dataConstants.NAVS.HOME}
                sx={{ fontSize: '0.8rem' }}
              >
                Home
              </Link>
              <Typography color="text.primary" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                Watchlists
              </Typography>
            </Breadcrumbs>
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            pb: 3,
          }}>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.5rem', sm: '1.75rem' }, lineHeight: 1.3 }}
              >
                {hero?.h1 ?? 'Watchlists'}
                {watchlists.length > 0 && (
                  <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '1rem', ml: 1 }}>
                    ({watchlists.length})
                  </Typography>
                )}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 480 }}
              >
                {hero?.desc ?? 'Track and compare stocks across curated and personal watchlists.'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <ToggleButtonGroup
                value={alignment}
                exclusive
                size="small"
                onChange={handleAlignment}
                aria-label="watchlist view"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1, py: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      color: 'primary.main',
                    },
                  },
                }}
              >
                <ToggleButton value="list" aria-label="list view">
                  <ViewListRoundedIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="grid" aria-label="grid view">
                  <ViewModuleRoundedIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
              <AddNewWatchlist
                handleNewWatchlistName={handleNewWatchlistName}
                dataEventFrom="homepage"
                isLoggedin={auth && auth?.user && auth?.user?.accessToken}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 4, pb: 16 }}>
        {isLoading ? (
          <Box>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={72}
                sx={{ mb: 1.5, borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : isEmpty ? (
          <Box sx={{
            textAlign: 'center',
            py: 12,
            px: 3,
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              No watchlists yet
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 360, mx: 'auto' }}>
              Create your first watchlist to start tracking and comparing stocks.
            </Typography>
            <AddNewWatchlist
              handleNewWatchlistName={handleNewWatchlistName}
              dataEventFrom="empty-state"
              isLoggedin={auth && auth?.user && auth?.user?.accessToken}
            />
          </Box>
        ) : alignment === 'list' ? (
          <ListView
            watchlists={watchlists}
            accessToken={user?.accessToken}
            getAllWatchlists={getAllWatchlists}
          />
        ) : (
          <GridView
            watchlists={watchlists}
            accessToken={user?.accessToken}
            getAllWatchlists={getAllWatchlists}
          />
        )}
      </Container>
    </Box>
  );
};

export async function getStaticProps({ params, tabCode = "WATCH", preview = false }) {
  const watch_tab_data = (await getStockTabs(undefined, tabCode, preview)) || [];
  const hero_data = (await getStockTabs("AAPL", "HERO")) || [];
  const { getStockTabs: hero } = hero_data || {};
  const meta_tags = (await getPageMetaTags(`/watchlist`, false)) || {};
  const { getMetaTags } = meta_tags || {};
  return {
    props: {
      meta_tags: getMetaTags?.tags || null,
      watch_tab_data,
      hero: getHeroContent(hero, "watchlists") || {},
    },
    revalidate: dataConstants.REVALIDATE_IN.ONE_DAY,
  };
}

export default Watchlist;
